// src/components/ComparedDocumentsList.tsx
import React, { useRef, useState } from 'react';
import { Collapse, Button } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import { Document, ComparisonResult } from '../types';
import ComparisonList from './comparison/ComparisonList';
import {colorBySimilarity} from "../services/utils/colorUtils";
import { useSimSettings } from '../hooks/useSimSettings';

const { Panel } = Collapse;

interface ComparedDocumentsListProps {
    /**
     * Словарь «docId -> массив ComparisonResult[]».
     * Например: { 'doc123': [ ... ], 'doc456': [ ... ] }
     */
    comparisonResults: Record<string, ComparisonResult[]>;

    /**
     * Если нужно отображать названия документов, можно передать словарь документов:
     * docId -> Document. Иначе используется дефолтное название.
     */
    documents?: Record<string, Document>;
}

const ComparedDocumentsList: React.FC<ComparedDocumentsListProps> = ({
                                                                         comparisonResults,
                                                                         documents,
                                                                     }) => {
    const { calcSim } = useSimSettings();

    const docIds = Object.keys(comparisonResults);
    const groupRefs = useRef<(HTMLDivElement | null)[]>([]);
    const [currentPanelIndex, setCurrentPanelIndex] = useState<number>(0);

    const getGroupAverage = (results: ComparisonResult[]): number => {
        if (results.length === 0) return 0;
        let total = 0;
        results.forEach((r) => { total += calcSim(r.similarity); });
        return total / results.length;
    };

    // Функция для прокрутки к следующей группе
    const handleScrollToNextPanel = () => {
        const nextIndex = (currentPanelIndex + 1) % groupRefs.current.length;
        groupRefs.current[nextIndex]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setCurrentPanelIndex(nextIndex);
    };

    return (
        <div className="p-4 bg-white rounded-lg shadow">
            {docIds.length === 0 ? (
                <div>Нет результатов для отображения.</div>
            ) : (
                docIds.map((docId, index) => {
                    const results = comparisonResults[docId];
                    const globalAvg = getGroupAverage(results);
                    const docTitle = documents?.[docId]?.title || `Документ #${docId}`;

                    // Заголовок Collapse.Panel: название, число результатов и средняя схожесть с цветовой индикацией
                    const header = (
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <div style={{ marginRight: 8 }}>
                                {docTitle} (результатов: {results.length}) — сред.: {Math.round(globalAvg * 100) / 100}
                            </div>
                            <div
                                style={{
                                    width: 12,
                                    height: 12,
                                    backgroundColor: colorBySimilarity(globalAvg),
                                    borderRadius: '50%',
                                }}
                            />
                        </div>
                    );

                    return (
                        <div
                            key={docId}
                            ref={(el) => (groupRefs.current[index] = el)}
                            style={{ marginBottom: '1rem' }}
                        >
                            <Collapse defaultActiveKey={['1']}>
                                <Panel header={header} key="1">
                                    {/* Используем универсальный компонент ComparisonList для отображения результатов */}
                                    <ComparisonList data={results} />
                                </Panel>
                            </Collapse>
                        </div>
                    );
                })
            )}

            {docIds.length > 1 && (
                <Button
                    onClick={handleScrollToNextPanel}
                    type="primary"
                    shape="circle"
                    icon={<DownOutlined />}
                    size="large"
                    style={{
                        position: 'sticky',
                        bottom: '10px',
                        right: '-10px',
                        zIndex: 10,
                    }}
                />
            )}
        </div>
    );
};

export default ComparedDocumentsList;
